import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/i18n";
import { PsychometricTest, PsychometricTestDetail } from "../types/exam-config.types";
import {
  listPsychometricTests,
  getPsychometricTestDetail,
  addPsychometricSection,
  removePsychometricSection,
} from "../services/psychometric.service";
import { examConfigKeys } from "./useExamConfig";

// ── Query keys ─────────────────────────────────────────────────────────────────

export const psychometricKeys = {
  all: ["psychometricTests"] as const,
  lists: () => [...psychometricKeys.all, "list"] as const,
  detail: (testId: string) => [...psychometricKeys.all, "detail", testId] as const,
};

// ── Hooks ──────────────────────────────────────────────────────────────────────

/**
 * Fetch all available psychometric tests from the test bank.
 */
export const usePsychometricTests = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<PsychometricTest[], Error>({
    queryKey: psychometricKeys.lists(),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await listPsychometricTests(accessToken);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as PsychometricTest[];
    },
    staleTime: 10 * 60 * 1000, // Tests don't change often — cache for 10 min
    gcTime: 30 * 60 * 1000,
  });
};

/**
 * Fetch a single psychometric test with full questions_data for preview.
 * Only fetches when testId is provided (non-empty string).
 */
export const usePsychometricTestDetail = (testId: string | null) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<PsychometricTestDetail, Error>({
    queryKey: psychometricKeys.detail(testId ?? ""),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getPsychometricTestDetail(accessToken, testId!);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as PsychometricTestDetail;
    },
    enabled: !!testId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

/**
 * Add a psychometric test section to the exam config for a job.
 * Invalidates the exam config query on success so the editor re-fetches.
 */
export const useAddPsychometricSection = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation<{ config_data: Record<string, unknown>; version: number }, Error, string>({
    mutationFn: async (testId: string) => {
      const accessToken = await getAccessTokenSilently();
      const response = await addPsychometricSection(accessToken, jobId, testId);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as { config_data: Record<string, unknown>; version: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: examConfigKeys.detail(jobId) });
      toast.success(i18n.t("interview:toasts.psychometric_test_added"));
    },
    onError: () => {
      toast.error(i18n.t("interview:toasts.psychometric_test_add_failed"));
    },
  });
};

/**
 * Remove a psychometric test section from the exam config for a job.
 * Invalidates the exam config query on success so the editor re-fetches.
 */
export const useRemovePsychometricSection = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation<{ config_data: Record<string, unknown>; version: number }, Error, string>({
    mutationFn: async (testId: string) => {
      const accessToken = await getAccessTokenSilently();
      const response = await removePsychometricSection(accessToken, jobId, testId);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as { config_data: Record<string, unknown>; version: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: examConfigKeys.detail(jobId) });
      toast.success(i18n.t("interview:toasts.psychometric_test_removed"));
    },
    onError: () => {
      toast.error(i18n.t("interview:toasts.psychometric_test_remove_failed"));
    },
  });
};
